<template>
  <CustomContainer
    v-if="showHeader"
    :padding="'p-0'"
    :height="'h-full'"
    :overflow-y="'overflow-y-auto'"
    class="w-full self-start"
  >
    <div class="flex flex-col h-full w-full">
      <div class="flex items-center justify-between px-3 py-2.5 border_bottom">
        <div class="flex items-center gap-2">
          <ThemeImage
            :icon="imageNames.AGI_PURPLE"
            class="size-5"
            alt="AI Card"
          />
          <span class="text-sm font-medium text_color">{{ displayName }}</span>
        </div>

        <div class="flex items-center gap-2">
          <a-dropdown v-if="currentState === 'display' && !disableActions" :trigger="['click']">
            <ThemeImage
              :icon="imageNames.HORIZONTAL_THREE_DOTS_GREY"
              class="size-5 cursor-pointer"
              alt="options"
            />
            <template #overlay>
              <a-menu>
                <a-menu-item @click="handleEditPrompt">
                  Edit Prompt
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>

          <ThemeImage
            v-if="showHeader && cardId && !disableActions"
            :icon="imageNames.DELETE_GREY"
            class="size-5 cursor-pointer hover:opacity-70"
            alt="delete"
            @click="handleDelete"
          />
        </div>
      </div>

      <div
        class="flex flex-col p-3 overflow-y-auto custom_scrollbar"
        :class="{ 'max-h-[310px]': !noMaxHeight }"
      >
        <AICardBody />
      </div>
    </div>
  </CustomContainer>

  <div
    v-else
    id="ai_card"
    class="flex flex-col h-full overflow-y-auto custom_scrollbar max-h-[460px] self-start"
  >
    <AICardBody />
  </div>

  <Teleport to="body">
    <div
      v-if="showImageModal"
      class="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-75 p-4"
      @click="closeImageModal"
    >
      <div class="relative max-w-4xl max-h-[90vh]">
        <img
          :src="selectedImage"
          alt="Full size image"
          class="max-w-full max-h-full rounded-lg"
        />
        <button
          @click.stop="closeImageModal"
          class="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
        >
          <ThemeImage
            :icon="imageNames.CLOSE_GREY"
            alt="close"
            class="size-6 invert"
          />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, provide, watch } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { convertMarkdownToHTML } from "@/src/utils/markdownUtils.js";
import { getPageContext, getObjectInfoFromURL } from "@/src/utils/htmlUtils.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import AICardBody from "@/src/components/dashboard_components/cards/AICardBody.vue";

const props = defineProps({
  source: { type: String, required: false, default: "" },
  cardId: { type: [Number, String], required: false, default: null },
  section: { type: Object, required: false, default: null },
  rowIndex: { type: Number, required: false, default: null },
  sectionIndex: { type: Number, required: false, default: null },
  noMaxHeight: { type: Boolean, required: false, default: false },
  disableActions: { type: Boolean, required: false, default: false },
  metadata: { type: Object, required: false, default: () => ({}) },
  globalUserId: { type: Number, default: null },
});

const emit = defineEmits(["card-created", "card-deleted"]);

const showHeader = computed(() => {
  return !!props.cardId || (!props.section && props.rowIndex === null);
});

const currentState = ref("edit");
const cardData = ref({
  id: null,
  name: null,
  prompt: null,
  response: null,
  json_data: null,
  images: null,
});
const editData = ref({
  name: "",
  prompt: "",
});
const selectedImage = ref(null);
const showImageModal = ref(false);
const isNewCard = ref(true);

const isEditing = computed(() =>
  props.rowIndex !== null && props.sectionIndex !== null
    ? store.getters["dashboard/isSectionEditing"](props.rowIndex, props.sectionIndex)
    : false,
);

const dashboardDetails = computed(
  () => store.getters["dashboard/dashboardDetails"],
);

const displayName = computed(() => {
  if (props.metadata?.component_name) {
    return props.metadata.component_name;
  }
  if (currentState.value === "edit" || currentState.value === "loading") {
    return editData.value.name || "Untitled Card";
  }
  return cardData.value.name || "Untitled Card";
});

const resolvedCardId = computed(() => {
  if (props.cardId !== null && props.cardId !== undefined) {
    return props.cardId;
  }
  if (props.section?.config?.card_id) {
    return props.section.config.card_id;
  }
  return null;
});

const formatMessage = (message) => {
  return message || "";
};

const isCompleteHtmlDocument = (content) => {
  if (!content) return false;
  const trimmed = content.trim().toLowerCase();
  return trimmed.startsWith("<!doctype html") || trimmed.startsWith("<html");
};

const processResponse = (response) => {
  if (!response) return response;
  // Skip markdown conversion if response is already a complete HTML document
  if (isCompleteHtmlDocument(response)) {
    return response;
  }
  return convertMarkdownToHTML(response);
};

const fetchCardData = async () => {
  const id = resolvedCardId.value;

  if (!id) {
    isNewCard.value = true;
    currentState.value = "edit";
    return;
  }

  if (isEditing.value) {
    currentState.value = "edit";
    return;
  }

  try {
    const response = await AnalyticService.V3.fetchAICardByID(id);
    if (response.data) {
      cardData.value = {
        ...response.data,
        response: processResponse(response.data.response),
      };

      isNewCard.value = false;
      editData.value = {
        name: response.data.name || "",
        prompt: response.data.prompt || "",
      };

      const hasContent =
        response.data.response ||
        response.data.json_data ||
        response.data.images;
      currentState.value = hasContent ? "display" : "edit";
    }
  } catch (error) {
    console.error("Failed to fetch AI card:", error);
    message.error("Failed to load AI card");
  }
};

const updateDashboardConfig = async (config) => {
  if (
    !props.section ||
    props.rowIndex === null ||
    props.sectionIndex === null
  ) {
    return;
  }

  try {
    await store.dispatch("dashboard/updateSectionConfig", {
      rowIndex: props.rowIndex,
      sectionIndex: props.sectionIndex,
      config,
    });

    const updatedLayout = store.getters["dashboard/dashboardLayout"];

    const payload = {
      name: dashboardDetails.value?.name,
      layout: updatedLayout,
    };

    const response = await DashboardService.builder.updateDashboard(
      dashboardDetails.value?.id,
      payload,
    );

    if (response?.success) {
      await store.dispatch("dashboard/setDashboardDetails", response.data);
    }
  } catch (error) {
    console.error("Failed to update dashboard config:", error);
    throw error;
  }
};

const handleSaveChanges = async () => {
  try {
    currentState.value = "loading";

    const payload = {
      name: editData.value.name,
      prompt: editData.value.prompt,
      context: getPageContext(),
    };

    const objectInfo = getObjectInfoFromURL();
    if (objectInfo) {
      payload.object_type = objectInfo.object_type;
      payload.object_id = objectInfo.object_id;
    }

    if (props.globalUserId !== null && props.globalUserId !== undefined) {
      payload.user_id = props.globalUserId;
    }

    let response;

    if (isNewCard.value) {
      response = await AnalyticService.V3.createAICard(payload);

      if (response.data) {
        const processedResponse =
          response.data.response && !response.data.web_search_used
            ? processResponse(response.data.response)
            : response.data.response;

        cardData.value = {
          ...response.data,
          response: processedResponse,
        };

        if (props.section) {
          await updateDashboardConfig({
            card_id: response.data.id,
            card_name: response.data.name,
          });
          store.dispatch("dashboard/clearSectionEditing", {
            rowIndex: props.rowIndex,
            sectionIndex: props.sectionIndex,
          });
        }

        isNewCard.value = false;
        message.success("AI card created successfully");

        if (showHeader.value) {
          emit("card-created");
        }

        const hasContent =
          response.data.response ||
          response.data.json_data ||
          response.data.images;
        currentState.value = hasContent ? "display" : "edit";
      }
    } else {
      const id = cardData.value.id || resolvedCardId.value;
      response = await AnalyticService.V3.updateAICardNamePrompt(id, payload);

      if (response.data) {
        const processedResponse =
          response.data.response && !response.data.web_search_used
            ? processResponse(response.data.response)
            : response.data.response;

        cardData.value = {
          ...response.data,
          response: processedResponse,
        };

        if (props.section) {
          await updateDashboardConfig({
            card_id: response.data.id,
            card_name: response.data.name,
          });
          store.dispatch("dashboard/clearSectionEditing", {
            rowIndex: props.rowIndex,
            sectionIndex: props.sectionIndex,
          });
        }

        message.success("AI card updated successfully");

        const hasContent =
          response.data.response ||
          response.data.json_data ||
          response.data.images;
        currentState.value = hasContent ? "display" : "edit";
      }
    }
  } catch (error) {
    console.error("Failed to save AI card:", error);
    message.error(`Failed to ${isNewCard.value ? "create" : "update"} AI card`);
    currentState.value = "edit";
  }
};

const handleCancel = async () => {
  try {
    if (isNewCard.value) {
      editData.value = {
        name: "",
        prompt: "",
      };
      currentState.value = "edit";
    } else {
      await fetchCardData();
    }
  } catch (error) {
    console.error("An error occurred while handling cancel: ", error);
  }
};

const handleEditPrompt = () => {
  try {
    editData.value = {
      name: cardData.value.name || "",
      prompt: cardData.value.prompt || "",
    };
    currentState.value = "edit";
  } catch (error) {
    console.error("An error occurred while handling edit prompt: ", error);
  }
};

const handleDelete = async () => {
  if (isNewCard.value) {
    emit("card-deleted");
    return;
  }

  try {
    const id = cardData.value.id || resolvedCardId.value;
    await AnalyticService.V3.deleteAICard(id);
    message.success("AI card deleted successfully");
    emit("card-deleted");
  } catch (error) {
    console.error("Failed to delete AI card:", error);
    message.error("Failed to delete AI card");
  }
};

const openImageModal = (imageUrl) => {
  try {
    selectedImage.value = imageUrl;
    showImageModal.value = true;
  } catch (error) {
    console.error("An error occurred while opening image image modal: ", error);
  }
};

const closeImageModal = () => {
  try {
    showImageModal.value = false;
    selectedImage.value = null;
  } catch (error) {
    console.error("An error occurred while closing image modal: ", error);
  }
};

provide("aiCardState", {
  currentState,
  editData,
  cardData,
  showHeader,
  formatMessage,
  handleCancel,
  handleSaveChanges,
  handleEditPrompt,
  openImageModal,
  imageNames,
});

watch(
  isEditing,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      fetchCardData();
    }
  },
);

onMounted(() => {
  fetchCardData();
});
</script>

<style scoped>
.custom_scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom_scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom_scrollbar::-webkit-scrollbar-thumb {
  background: var(--white-opacity-8);
  border-radius: 3px;
}

.custom_scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--white-opacity-16);
}
</style>
